import {
  attachSignedFileUrls,
  getOrganizationColor,
  getTintedOrganizationColor,
} from "helpers/shared.helpers";
import React, {
  createContext,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from "react";
import FileService from "services/File.service";
import LocationService from "services/Location.service";
import OrganizationService from "services/Organization.service";
import OtherContactService from "services/OtherContact.service";
import PersonService from "services/Person.service";
import TeamService from "services/Team.service";
import {
  actionTypes,
  initialState,
  reducer,
} from "web/reducers/organization.reducer";

export const OrganizationContext = createContext();

export const OrganizationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentRoute, setCurrentRoute] = useState(null);

  const getOrganization = useCallback(async () => {
    try {
      dispatch({ type: actionTypes.SET_ORGANIZATION_LOADING, payload: true });
      const res = await OrganizationService.getOrganization();
      const data = await attachSignedFileUrls(res.payload);
      dispatch({ type: actionTypes.SET_ORGANIZATION, payload: data });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    } finally {
      dispatch({ type: actionTypes.SET_ORGANIZATION_LOADING, payload: false });
    }
  }, []);

  const getPersons = useCallback(async (includeDeleted = false) => {
    try {
      const res = await PersonService.getPersons(includeDeleted);
      dispatch({ type: actionTypes.SET_PERSONS, payload: res.payload });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const getOtherContacts = useCallback(async () => {
    try {
      const res = await OtherContactService.getOtherContacts();
      dispatch({
        type: actionTypes.SET_OTHER_CONTACT_LIST,
        payload: res.payload,
      });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const getUsersTeamsPaginated = useCallback(async (...args) => {
    try {
      const res = await TeamService.getUsersTeamsPaginated(...args);
      const data = await attachSignedFileUrls(res.payload);
      dispatch({ type: actionTypes.SET_USERS_TEAMS_PAGINATED, payload: data });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const getLocations = useCallback(async () => {
    try {
      const res = await LocationService.getLocations();
      const data = await attachSignedFileUrls(res.payload);
      dispatch({ type: actionTypes.SET_LOCATION_LIST, payload: data });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const getTeams = useCallback(async () => {
    try {
      const res = await TeamService.getTeams();
      dispatch({ type: actionTypes.SET_TEAM_LIST, payload: res.payload });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const getOrganizationResources = useCallback(async () => {
    try {
      const res = await OrganizationService.getOrganizationResources();
      const data = await attachSignedFileUrls(res.payload);
      dispatch({ type: actionTypes.SET_ORG_RESOURCES, payload: data });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const getDefaultPlatformFiles = useCallback(async () => {
    try {
      const res = await FileService.getDefaultImages();

      const filesWithUrls = await Promise.all(
        res.payload.map(async (file) => {
          try {
            const urlRes = await FileService.getSignedFileUrl(file.fileId);
            return {
              ...file,
              signedUrl: urlRes.payload,
            };
          } catch (error) {
            console.error(
              `Failed to get signed URL for file ${file.fileId}:`,
              error,
            );
            return file;
          }
        }),
      );

      dispatch({
        type: actionTypes.SET_DEFAULT_PLATFORM_FILES,
        payload: filesWithUrls,
      });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const getOrganizationSystemDependencies = useCallback(async () => {
    try {
      const res = await OrganizationService.getOrganizationSystemDependencies();
      dispatch({
        type: actionTypes.SET_ORG_SYSTEM_DEPENDENCIES,
        payload: res.payload,
      });
    } catch (error) {
      console.error(error?.response?.data ?? error?.message);
    }
  }, []);

  const getOrganizationSystems = useCallback(async () => {
    try {
      const res = await OrganizationService.getOrganizationSystems();
      const data = await attachSignedFileUrls(res.payload);
      dispatch({ type: actionTypes.SET_ORG_SYSTEMS, payload: data });
    } catch (error) {
      if (__DEV__) {
        console.error(error?.response?.data ?? error?.message);
      }
    }
  }, []);

  const upsertOrganizationSystem = useCallback(
    async (system, dependents) => {
      await OrganizationService.upsertOrganizationSystem({
        system,
        dependents,
      });
      await getOrganizationSystemDependencies();
      await getOrganizationSystems();
    },
    [getOrganizationSystemDependencies, getOrganizationSystems],
  );

  const deleteOrganizationSystem = useCallback(async (system) => {
    const res = await OrganizationService.deleteOrganizationSystem(
      system.organizationSystemId,
    );
    dispatch({ type: actionTypes.DELETE_ORG_SYSTEM, payload: res.payload });
  }, []);

  const updateContactInformation = useCallback(
    (contactId, infoList, updatedUser) => {
      dispatch({
        type: actionTypes.UPDATE_CONTACT_INFORMATION,
        payload: { contactId, infoList, updatedUser },
      });
    },
    [],
  );

  const upsertOrganizationResource = useCallback(
    async (resource) => {
      await OrganizationService.upsertOrganizationResource({ resource });
      await getOrganizationResources();
    },
    [getOrganizationResources],
  );

  const deleteOrganizationResource = useCallback(async (resource) => {
    const res = await OrganizationService.deleteOrganizationResource(
      resource.resourceId,
    );
    dispatch({ type: actionTypes.DELETE_ORG_RESOURCE, payload: res.payload });
  }, []);

  const deleteOrganization = useCallback(async () => {
    try {
      return await OrganizationService.deleteOrganization();
    } catch (error) {
      if (__DEV__) {
        console.error(error);
      }
      throw error;
    }
  }, []);

  const { organization, organizationSystems, organizationSystemDependencies } =
    state;

  const organizationColor = useMemo(
    () => getOrganizationColor(organization?.colorScheme),
    [organization],
  );
  const organizationTint = useMemo(
    () => getTintedOrganizationColor(organization?.colorScheme, 20),
    [organization],
  );

  const personsByTeam = useMemo(() => {
    const persons = state.personList || [];
    const teams = state.teamList || [];
    const map = {};
    teams.forEach((team) => {
      map[team.teamId] = persons.filter((person) =>
        person._associations?.Teams?.some(
          (aTeam) => aTeam.teamId === team.teamId,
        ),
      );
    });
    return map;
  }, [state.personList, state.teamList]);

  const personsByLocation = useMemo(() => {
    const persons = state.personList || [];
    const locations = state.locationList || [];
    const map = {};
    locations.forEach((location) => {
      map[location.locationId] = persons.filter((person) =>
        person._associations?.Locations?.some(
          (aLocation) => aLocation.locationId === location.locationId,
        ),
      );
    });
    return map;
  }, [state.personList, state.locationList]);

  const contextValue = useMemo(
    () => ({
      ...state,
      dispatch,
      organizationColor,
      organizationTint,
      currentRoute,
      getOtherContacts,
      setCurrentRoute,
      getOrganization,
      deleteOrganization,
      getOrganizationSystems,
      upsertOrganizationSystem,
      deleteOrganizationSystem,
      getOrganizationSystemDependencies,
      getTeams,
      updateContactInformation,
      getLocations,
      getOrganizationResources,
      upsertOrganizationResource,
      deleteOrganizationResource,
      getPersons,
      getUsersTeamsPaginated,
      getDefaultPlatformFiles,
      organizationSystemMap: new Map(
        organizationSystems.map((system) => [
          system.organizationSystemId,
          system,
        ]),
      ),
      organizationSystemDependenciesMap: new Map(
        organizationSystemDependencies.map((dep) => [
          dep.organizationSystemDependencyId,
          dep,
        ]),
      ),
      personsByTeam,
      personsByLocation,
    }),
    [
      state,
      dispatch,
      organizationColor,
      organizationTint,
      currentRoute,
      setCurrentRoute,
      getOrganization,
      deleteOrganization,
      getOrganizationSystems,
      upsertOrganizationSystem,
      deleteOrganizationSystem,
      getOrganizationSystemDependencies,
      getTeams,
      updateContactInformation,
      getLocations,
      getOrganizationResources,
      upsertOrganizationResource,
      deleteOrganizationResource,
      getPersons,
      getOtherContacts,
      getUsersTeamsPaginated,
      getDefaultPlatformFiles,
      organizationSystems,
      organizationSystemDependencies,
      personsByTeam,
      personsByLocation,
    ],
  );

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};
